import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Theme } from '@material-ui/core/styles';

import { useGetSimilarListingBySlug } from '@keaze/web/hooks/queries/listings/useGetSimilarListingBySlug';
import { useGetListingBySlugQuery } from '@keaze/web/hooks/queries/listings/useGetListingBySlug';
import { setInterestRegister } from '@keaze/web/store/startInterestRegister';
import { AuthContext } from '@keaze/web/contexts/auth';
import { OnRegisterButtonClick } from './listingDetails.types';
import {
  LocalStorageKeys,
  QueryParams,
  OccupancyType,
  QueryModals,
} from '@keaze/web/common/enums';
import { useLocalStorage, UTMParams } from '@keaze/web/hooks';
import { OnMapClick } from './map';
import { OnVideoClick } from '@keaze/web/components/videos';
import {
  StoreContext,
  setBackButtonTo,
  resetBackButtonTo,
} from '@keaze/web/store';
import { onQueryDialogClick } from '@keaze/web/utils/queryDialog';
import {
  Listing,
  ListingDetails,
  PersonalizedSearchForm,
  RegisterInterestSavedData,
} from '@keaze/web/common/interfaces';
import { GTMInterestRegistration, GTMViewListing } from '@keaze/web/libs/GTM';
import { useRegisterInterestMutate } from '@keaze/web/hooks/queries/listings/useCreateRegisterInterest';
import { Methods } from '@keaze/web/api';
import { StoredFilters } from '../search/filters';
import { DEFAULT_PERSONALIZED_SEARCH_DATA } from '@keaze/web/utils/defaultValues';
import { getSearchParamsForListing } from '@keaze/web/utils/params';

const REGISTER_INTEREST_PATHNAME = 'register-interest';

type UseListingDetailsExpected = {
  registerInterestOpen: boolean;
  toggleRegisterInterestDialog: () => void;
  address: string;
  listingData: ListingDetails;
  similarData: Listing[];
  isRegisterInterestLoading: boolean;
  isListingLoading: boolean;
  onVideoClick: OnVideoClick;
  onMapClick: OnMapClick;
  onRegisterButtonClick: OnRegisterButtonClick;
};

type UseListingDetails = (
  listing: ListingDetails,
  similar: Listing[]
) => UseListingDetailsExpected;

type PushRegisterInterest = (propertyId: number | null) => void;

type OnBackButtonToSearch = (occupancyType: OccupancyType) => void;

type GenerateAddress = (
  displayAddress?: string,
  postcode?: string | null
) => string;

const generateAddress: GenerateAddress = (displayAddress = '', postcode) => {
  const generatedPostCode = postcode ? `, ${postcode}` : '';

  return `${displayAddress}${generatedPostCode}`;
};

export const useListingDetails: UseListingDetails = (listing, similar) => {
  const router = useRouter();
  const isMedium = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const isRegisterInterestFailed = !!router.query[
    QueryParams.RegisterInterestFailed
  ];
  const listingSlug = router.query[QueryParams.ListingSlug] as string;
  const { token, isAuthenticated } = useContext(AuthContext);
  const [registerInterestOpen, setRegisterInterestOpen] = useState<boolean>(
    false
  );
  const [storedUTMParams] = useLocalStorage<
    UTMParams | Record<string, unknown>
  >(LocalStorageKeys.UTMParams, {});
  const propertyId = useRef<number | null>(null);

  const {
    data: listingData,
    isFetching: isListingFetching,
    isLoading: isListingLoading,
  } = useGetListingBySlugQuery({
    token,
    listingSlug,
    initialData: listing,
  });
  const {
    data: similarData,
    isFetching: isSimilarFetching,
    isLoading: isSimilarLoading,
  } = useGetSimilarListingBySlug({
    token,
    listingSlug,
    initialData: similar,
  });
  const {
    data: registerInterestData,
    mutate: registerInterestMutate,
    isSuccess: isRegisterInterestSuccess,
    isLoading: isRegisterInterestLoading,
    isError: isRegisterInterestError,
  } = useRegisterInterestMutate();

  const [
    { householdIncome, estimatedDeposit },
  ] = useLocalStorage<PersonalizedSearchForm>(
    LocalStorageKeys.personalizedSearch,
    DEFAULT_PERSONALIZED_SEARCH_DATA
  );
  const [storedFilters] = useLocalStorage<StoredFilters | null>(
    LocalStorageKeys.filters,
    null
  );

  const { dispatch } = useContext(StoreContext);

  const address = generateAddress(
    listingData?.displayAddress,
    listingData?.postcode
  );

  const setRegisterInterestSavedData = async (
    value: RegisterInterestSavedData
  ) => {
    await fetch('/api/registerInterest/set', {
      method: Methods.POST,
      body: JSON.stringify(value),
    });
  };

  const pushRegisterInterest: PushRegisterInterest = useCallback(
    (propertyId) => {
      if (propertyId) {
        return router.push(
          `/${REGISTER_INTEREST_PATHNAME}/${listingSlug}/${QueryParams.Property}/${propertyId}`
        );
      }

      return router.push(`/${REGISTER_INTEREST_PATHNAME}/${listingSlug}`);
    },
    [router, listingSlug]
  );

  const handleBackButtonToSearch: OnBackButtonToSearch = useCallback(
    (occupancyType) => {
      router.push({
        pathname: '/search',
        query: {
          [QueryParams.OccupancyType]: occupancyType,
          ...getSearchParamsForListing(router.query),
        },
      });
    },
    [router]
  );

  useEffect(() => {
    GTMViewListing({
      event: 'view_listing',
      listingSchemes: listing.schemes,
      listingSlug: listing.slug,
      householdIncome,
      estimatedDeposit,
    });
  }, [listing, householdIncome, estimatedDeposit]);

  useEffect(() => {
    if (isRegisterInterestFailed) {
      setRegisterInterestOpen(true);

      GTMInterestRegistration({
        event: 'duplicated_interest_registration',
        listingSchemes: listing.schemes,
        listingSlug: listing.slug,
        propertyId: propertyId.current,
        location: storedFilters && storedFilters.location,
        householdIncome,
        estimatedDeposit,
      });
    }
  }, [
    isRegisterInterestFailed,
    listing,
    storedFilters,
    householdIncome,
    estimatedDeposit,
  ]);

  useEffect(() => {
    const backButtonToName = isMedium ? 'Back to search' : 'Back';
    const generatedOccupancyType = listing.forSale
      ? OccupancyType.Sale
      : OccupancyType.Rent;

    dispatch(
      setBackButtonTo({
        name: backButtonToName,
        onClick: () => handleBackButtonToSearch(generatedOccupancyType),
      })
    );

    return () => {
      dispatch(resetBackButtonTo());
    };
  }, [isMedium, listing, dispatch, handleBackButtonToSearch]);

  useEffect(() => {
    if (isRegisterInterestSuccess && registerInterestData) {
      const value: RegisterInterestSavedData = {
        applicationId: registerInterestData.id,
        updateToken: registerInterestData.updateToken,
        slug: null,
        propertyId: null,
      };

      GTMInterestRegistration({
        event: 'interest_registration',
        listingSchemes: listing.schemes,
        listingSlug: listing.slug,
        propertyId: propertyId.current,
        location: storedFilters && storedFilters.location,
        householdIncome,
        estimatedDeposit,
      });

      setRegisterInterestSavedData(value);
      pushRegisterInterest(propertyId.current);
    }
  }, [
    isRegisterInterestSuccess,
    registerInterestData,
    listing,
    storedFilters,
    householdIncome,
    estimatedDeposit,
    pushRegisterInterest,
  ]);

  useEffect(() => {
    if (isRegisterInterestError) {
      router.replace(
        {
          query: {
            ...router.query,
            [QueryParams.RegisterInterestFailed]: 'true',
          },
        },
        undefined,
        {
          shallow: true,
        }
      );
    }
  }, [isRegisterInterestError]);

  const handleVideoClick: OnVideoClick = useCallback(
    (id) => onQueryDialogClick({ router, name: QueryModals.video, value: id }),
    [router]
  );

  const handleMapClick: OnMapClick = useCallback(
    (value) => onQueryDialogClick({ router, name: QueryModals.map, value }),
    [router]
  );

  const toggleRegisterInterestDialog = () =>
    setRegisterInterestOpen((prevState) => !prevState);

  const handleRegisterButtonClick: OnRegisterButtonClick = useCallback(
    (propertyId, externalRef) => {
      localStorage.removeItem('individualPropertyID');
      localStorage.setItem(
        'individualPropertyID',
        // (externalRef as unknown) as string
        externalRef
      );
      GTMInterestRegistration({
        event: 'start_interest_registration',
        listingSchemes: listing.schemes,
        listingSlug: listing.slug,
        propertyId: propertyId ?? null,
        location: storedFilters && storedFilters.location,
        householdIncome,
        estimatedDeposit,
      });

      if (isAuthenticated) {
        return registerInterestMutate({
          token,
          slug: listingSlug,
          data: {
            ...storedUTMParams,
            property: propertyId,
          },
        });
      }

      dispatch(
        setInterestRegister({
          address,
          name: listing.name,
          schemes: listing.schemes,
          slug: listing.slug,
          externalRef: externalRef,
          propertyId: propertyId ?? null,
        })
      );

      toggleRegisterInterestDialog();
    },
    [
      token,
      listing,
      storedUTMParams,
      address,
      listingSlug,
      isAuthenticated,
      storedFilters,
      householdIncome,
      estimatedDeposit,
      dispatch,
      registerInterestMutate,
    ]
  );

  return {
    registerInterestOpen,
    toggleRegisterInterestDialog,
    address,
    listingData: listingData as ListingDetails,
    similarData: similarData as Listing[],
    isRegisterInterestLoading,
    isListingLoading:
      isListingLoading ||
      isListingFetching ||
      isSimilarLoading ||
      isSimilarFetching,
    onVideoClick: handleVideoClick,
    onMapClick: handleMapClick,
    onRegisterButtonClick: handleRegisterButtonClick,
  };
};
